import { useEffect, useMemo, useRef, useState } from 'react'
import { ECurecallNotificationAppearance } from '@honestica/curecall-ui'
import { TFollowup, TPatientFollowup, TValidationRule } from 'src/types/followup'
import { TMedEventUI } from 'src/types/med-event'
import { TPatient } from 'src/types/patient'
import { TDeprecatedPatientVariable } from 'src/types/variable'
// TPlanningPatientMedEvent type - using any for now
type TPlanningPatientMedEvent = any
import {
  getAddableMedEventsByType,
  getInitialCompletedMedEvent,
  hasCompletedMedEventsChanged,
  sortCompletedMedEvent,
} from './generic-followup-form.helper'
import { getValidationErrors, saveFollowup } from './generic-followup-form.service'
import { getFormattedDateWithHours } from 'src/utils/date-formatter.util'
import { useNotification } from 'src/providers/notification.provider'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { MedEventFormRef, TCompletedMedEvent, TSelectedMedEvent } from './generic-followup-form.type'
import { useFollowupModalContext } from './generic-followup-modal.component'
import { useCurrentProject } from 'src/providers/current-project.provider'

type UseGenericFollowupFormProps = {
  followup: TFollowup
  patient: TPatient
  patientFollowupIdEdited?: number
  onCancel?: (event?: React.MouseEvent<HTMLElement>, isSubmittingForm?: boolean) => void
}

export const useGenericFollowupForm = ({
  followup,
  patient,
  patientFollowupIdEdited,
  onCancel,
}: UseGenericFollowupFormProps) => {
  const { t } = useTranslation()
  const configUI = followup.configUI
  const navigate = useLocalizedNavigate()
  const { project, getMedEventLabelById } = useCurrentProject()
  const { notify } = useNotification()
  const queryClient = useQueryClient()
  const { setHasFormBeenModified } = useFollowupModalContext()

  // State
  const medEventFormRef = useRef<MedEventFormRef>(null)
  const [patientVariables, setPatientVariables] = useState<TDeprecatedPatientVariable[]>([])
  const [requiredMedEvents, setRequiredMedEvents] = useState<TMedEventUI[]>([])
  const [recommendedMedEvents, setRecommendedMedEvents] = useState<TMedEventUI[]>([])
  const [optionalMedEvents, setOptionalMedEvents] = useState<TMedEventUI[]>([])
  const [selectedMedEvent, setSelectedMedEvent] = useState<TSelectedMedEvent | undefined>()
  const [completedMedEvents, setCompletedMedEvents] = useState<TCompletedMedEvent[]>([])
  const [initialCompletedMedEvents, setInitialCompletedMedEvents] = useState<TCompletedMedEvent[]>([])
  const [medEventToDelete, setMedEventToDelete] = useState<TCompletedMedEvent>()
  const [deletedPatientMedEventIds, setDeletedPatientMedEventIds] = useState<number[]>([])
  const [validationErrors, setValidationErrors] = useState<TValidationRule[] | undefined>([])
  const [isDeleteMedEventModalOpened, setIsDeleteMedEventModalOpened] = useState<boolean>(false)

  // Initialize med events when configUI or patient changes
  useEffect(() => {
    if (!configUI) return
    const allPatientMedEvents = patient?.PatientMedEvents || []
    const completedPatientMedEventsForThisPatientFollowup = allPatientMedEvents.filter((patientMedEvent) => {
      return patientMedEvent.PatientFollowupId === patientFollowupIdEdited
    })
    const completedPatientMedEvents = getInitialCompletedMedEvent(
      configUI.medEventsUI,
      completedPatientMedEventsForThisPatientFollowup,
    )
    setCompletedMedEvents(completedPatientMedEvents)
    setInitialCompletedMedEvents(completedPatientMedEvents) // Sauvegarder l'état initial

    const { requiredMedEvents, recommendedMedEvents, optionalMedEvents } = getAddableMedEventsByType({
      configUI,
      completedMedEvents: completedPatientMedEvents,
    })
    setRequiredMedEvents(requiredMedEvents)
    setRecommendedMedEvents(recommendedMedEvents)
    setOptionalMedEvents(optionalMedEvents)

    if (requiredMedEvents?.[0]) {
      setSelectedMedEvent(requiredMedEvents[0])
    }
  }, [patient, configUI, followup?.id, patientFollowupIdEdited])

  useEffect(() => {
    if (!setHasFormBeenModified) return
    const formHasBeenModified =
      hasCompletedMedEventsChanged(completedMedEvents, initialCompletedMedEvents) ||
      selectedMedEvent !== undefined ||
      deletedPatientMedEventIds.length > 0

    setHasFormBeenModified(formHasBeenModified)
  }, [
    completedMedEvents,
    initialCompletedMedEvents,
    selectedMedEvent,
    deletedPatientMedEventIds,
    setHasFormBeenModified,
  ])

  // Handlers
  const updatePatientVariableList = (
    variableToAddOrUpdate: TDeprecatedPatientVariable,
    isChecked: boolean,
  ) => {
    const isInList = patientVariables.some(
      (variable) => variable.variableName === variableToAddOrUpdate.variableName,
    )
    const listUpdated = isInList
      ? patientVariables.map((variable) => {
          if (variable.variableName === variableToAddOrUpdate.variableName) {
            variable.value = isChecked
          }
          return variable
        })
      : [...patientVariables, { ...variableToAddOrUpdate, value: isChecked }]
    setPatientVariables(listUpdated)
  }

  const openConfirmModalToDeleteMedEvent = (medEventToDelete: TCompletedMedEvent) => {
    setMedEventToDelete(medEventToDelete)
    setIsDeleteMedEventModalOpened(true)
  }

  const onDeleteCard = () => {
    if (medEventToDelete) {
      const newListOfCompletedMedEvent = completedMedEvents.filter(
        (medEvent) => medEvent.uniqId !== medEventToDelete.uniqId,
      )
      setCompletedMedEvents(newListOfCompletedMedEvent)

      const { requiredMedEvents, recommendedMedEvents, optionalMedEvents } = getAddableMedEventsByType({
        configUI,
        completedMedEvents: newListOfCompletedMedEvent,
      })
      setRequiredMedEvents(requiredMedEvents)
      setRecommendedMedEvents(recommendedMedEvents)
      setOptionalMedEvents(optionalMedEvents)

      if (requiredMedEvents?.[0]) {
        setSelectedMedEvent(requiredMedEvents[0])
      }

      medEventToDelete.patientMedEventId &&
        setDeletedPatientMedEventIds([...deletedPatientMedEventIds, medEventToDelete.patientMedEventId])

      setMedEventToDelete(undefined)
    }
    setIsDeleteMedEventModalOpened(false)
  }

  const selectNewMedEventToComplete = (medEvent: TMedEventUI) => {
    if (selectedMedEvent) {
      setSelectedMedEvent({
        ...selectedMedEvent,
        errorMessage: t('components.generic-followups-form.thanks-to-finish-this-form-first'),
      })
      return false
    }
    setSelectedMedEvent(medEvent)
    return true
  }

  const addOrUpdateCompletedMedEvent = (newCompletedMedEvent: TCompletedMedEvent) => {
    let newListOfCompletedMedEvent = []
    const isUpdated = completedMedEvents.some((medEvent) => medEvent.uniqId === newCompletedMedEvent.uniqId)
    if (isUpdated) {
      newListOfCompletedMedEvent = completedMedEvents.map((medEvent) => {
        return medEvent.uniqId === newCompletedMedEvent.uniqId ? newCompletedMedEvent : medEvent
      })
    } else {
      newListOfCompletedMedEvent = sortCompletedMedEvent([...completedMedEvents, newCompletedMedEvent])
      const { requiredMedEvents, recommendedMedEvents, optionalMedEvents } = getAddableMedEventsByType({
        configUI,
        completedMedEvents: newListOfCompletedMedEvent,
      })
      setRequiredMedEvents(requiredMedEvents)
      setRecommendedMedEvents(recommendedMedEvents)
      setOptionalMedEvents(optionalMedEvents)
    }
    setSelectedMedEvent(undefined)
    setCompletedMedEvents(newListOfCompletedMedEvent)
    return newListOfCompletedMedEvent // return directly the value without waiting state to be updated
  }

  const cancelMedEventToComplete = () => {
    if (selectedMedEvent) {
      setSelectedMedEvent(undefined)
    }
  }

  const onSubmit = async () => {
    let completedMedEventListUpdated = completedMedEvents
    if (selectedMedEvent) {
      const newOrUpdatedCompletedMedEvent = await medEventFormRef.current?.getFormValues()
      if (!newOrUpdatedCompletedMedEvent) {
        return
      }
      // validate opened form and get completed med events with the new or updated one
      completedMedEventListUpdated = addOrUpdateCompletedMedEvent(newOrUpdatedCompletedMedEvent)
    }
    try {
      // we only check min validation rule at submit because we don't want to show validation error before
      const validationRulesNotRespected = getValidationErrors(completedMedEventListUpdated, configUI)
      if (validationRulesNotRespected && validationRulesNotRespected.length > 0) {
        setValidationErrors(validationRulesNotRespected)
        return
      }
      if (!followup) {
        throw new Error(t('components.generic-followups-form.project-followup-not-found'))
      }
      const patientUpdatedResult: TPatient = await saveFollowup({
        patientVariables,
        completedMedEvents: completedMedEventListUpdated,
        deletedPatientMedEventIds,
        patient,
        followupId: followup.id,
        patientFollowupId: patientFollowupIdEdited,
      })

      // Close the modal BEFORE updating the cache. If the backend deleted the PatientFollowup
      // (because it became empty after all med events were removed), updating the cache first
      // would trigger a re-render of GenericFollowupModal that shows "followup not found"
      // while the modal is still mounted.
      if (patientFollowupIdEdited) {
        onCancel?.(undefined, true)
      }

      queryClient.setQueryData(['patients', patient.ProjectId, patient.id], () => patientUpdatedResult)

      // update timeline and patient variables :
      queryClient.removeQueries({ queryKey: ['patient-timeline', patient.ProjectId, patient.id] })
      setTimeout(async () => {
        await queryClient.invalidateQueries({ queryKey: ['patient-timeline', patient.ProjectId, patient.id] })
      }, 3000) // timeline element are generated asynchrously by the worker , so we should wait a bit before quering again the timeline and getting updated elements
      await queryClient.invalidateQueries({
        queryKey: ['patients-all-variables', patient.ProjectId, patient.id],
      })
      // update planning
      queryClient
        .getQueriesData({ queryKey: ['planning-patient-med-events'] })
        .forEach(async ([queryKey, queryData]: any) => {
          const planningPatientMedEvents = queryData?.patientMedEvents || []
          const hasAPatientMedEventOfListBeenUpdated = planningPatientMedEvents.some(
            (planningPatientMedEvent: TPlanningPatientMedEvent) =>
              patientUpdatedResult.PatientMedEvents.some((pme) => pme.id === planningPatientMedEvent.id),
          )
          if (hasAPatientMedEventOfListBeenUpdated) {
            await queryClient.invalidateQueries(queryKey)
          }
        })

      const lastCreatedPatientFollowup = patientUpdatedResult?.PatientFollowups?.sort(
        (pf1: TPatientFollowup, pf2: TPatientFollowup) =>
          new Date(pf2.createdAt).getTime() - new Date(pf1.createdAt).getTime(),
      )?.[0] as TPatientFollowup

      if (!patientFollowupIdEdited) {
        navigate(`/${patient.ProjectId}/medical/patients/${patient.id}/${lastCreatedPatientFollowup.id}`)
      }

      patientFollowupIdEdited
        ? notify({
            title: t('components.generic-followups-form.followup-updated-success-title'),
            message: t('components.generic-followups-form.followup-updated-success-message', {
              followupName: followup.name,
            }),
            appearance: ECurecallNotificationAppearance.SUCCESS,
          })
        : notify({
            title: t('components.generic-followups-form.followup-added-success-title'),
            message: t('components.generic-followups-form.followup-added-success-message', {
              followupName: followup.name,
            }),
            appearance: ECurecallNotificationAppearance.SUCCESS,
          })
    } catch (e: any) {
      notify({
        title: t('components.generic-followups-form.error-message'),
        message: e,
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    }
  }

  const addNewFollowup = () => {
    navigate(`/${project.id}/medical/patients/${patient.id}/add-patient-followup`)
    onCancel?.()
  }

  // Modal handlers
  const closeDeleteModal = () => setIsDeleteMedEventModalOpened(false)

  const getDeleteModalContent = () => {
    if (!medEventToDelete) return ''

    return medEventToDelete?.date
      ? t('components.generic-followups-form.medevent-date-and-time', {
          medeventLabel: getMedEventLabelById(medEventToDelete?.medEventUI?.MedEvent.id),
          dateAndTime: getFormattedDateWithHours(t, medEventToDelete.date),
        })
      : getMedEventLabelById(medEventToDelete?.medEventUI?.MedEvent.id)
  }

  const formContextValue = useMemo(
    () => ({
      selectedMedEvent,
      medEventFormRef,
      configUI,
      patient,
      validationErrors,
      requiredMedEvents,
      recommendedMedEvents,
      optionalMedEvents,
      completedMedEvents,
      openConfirmModal: openConfirmModalToDeleteMedEvent,
      cancelMedEventToComplete,
      addOrUpdateCompletedMedEvent,
      selectNewMedEventToComplete,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedMedEvent,
      configUI,
      patient,
      validationErrors,
      requiredMedEvents,
      recommendedMedEvents,
      optionalMedEvents,
      completedMedEvents,
    ],
  )

  return {
    // states
    formContextValue,
    isDeleteMedEventModalOpened,

    // Handlers
    updatePatientVariableList,
    openConfirmModal: openConfirmModalToDeleteMedEvent,
    cancelMedEventToComplete,
    addOrUpdateCompletedMedEvent,
    selectNewMedEventToComplete,
    onSubmit,
    addNewFollowup,

    // Modal handlers
    onDeleteCard,
    closeDeleteModal,
    getDeleteModalContent,
  }
}
